// The Admin's 911 page and 911 contacts (docs/specs/telephony-compliance.md sections 4.1 and
// 4.3, stories TC12, TC14, TC15). Mirrors crates/api-types/src/emergency_location.rs and the
// contacts shapes in crates/server/src/emergency/http.rs. Admins only: every route here
// answers 403 to anyone else. Exported as the `e911Admin` namespace, so these names never meet
// the person's own `/v1/me/emergency-location` types.

import type { ApiTransport } from "./client.ts";
import type { Timestamp, Uuid } from "./types.ts";

/** How Kite knows where a device is. */
export type LocationSource = "office_network" | "device" | "saved" | "company" | "picked";

/** An office (`company`) or a person's saved address (`personal`). */
export type PlaceKind = "company" | "personal";

export interface EmergencyPlace {
  id: Uuid;
  kind: PlaceKind;
  /** "Main office", "Home". */
  label: string;
  /** One line: street, unit, city, state. */
  address: string;
}

export type NetworkState = "proposed" | "confirmed" | "dismissed";

export interface OfficeNetwork {
  id: Uuid;
  /** `203.0.113.0/24`, `2001:db8:1:2::/64`. */
  cidr: string;
  state: NetworkState;
  /** The office, once confirmed. */
  office_id?: Uuid | null;
  decided_at?: Timestamp | null;
}

export interface Office {
  place: EmergencyPlace;
  /** Confirmed (and dismissed) networks of this office. */
  networks: OfficeNetwork[];
}

/** Where Kite currently places one person. */
export interface PersonPlace {
  user_id: Uuid;
  name: string;
  email: string;
  /** Signed in only on phones: 911 goes through the phone's dialer. */
  phone_app_only: boolean;
  place?: EmergencyPlace | null;
  source?: LocationSource | null;
  since?: Timestamp | null;
}

/** `GET /v1/admin/emergency-locations`. */
export interface EmergencyLocationsPage {
  offices: Office[];
  /** Networks Kite proposes from where people sign in, to confirm at an office or dismiss. */
  proposals: OfficeNetwork[];
  people: PersonPlace[];
}

/** `POST /v1/admin/emergency-locations/networks`: added by hand, confirmed at once. */
export interface NewOfficeNetwork {
  cidr: string;
  office_id: Uuid;
}

/** A member who gets the 911 notice. */
export interface ContactMember {
  user_id: Uuid;
  name: string;
  email: string;
}

/** `GET /v1/admin/emergency-contacts`. */
export interface EmergencyContacts {
  members: ContactMember[];
  /** No member picked: every Admin gets the notice. */
  default_admins: boolean;
  emails: string[];
  /** The most outside addresses allowed. */
  max_emails: number;
}

/** `PUT /v1/admin/emergency-contacts`: replaces both lists. No members means every Admin. */
export interface SetEmergencyContacts {
  members: Uuid[];
  emails: string[];
}

const enc = encodeURIComponent;
const BASE = "/v1/admin/emergency-locations";

export class EmergencyAdminApi {
  constructor(private readonly client: ApiTransport) {}

  /** Offices with their networks, proposals, and where Kite places each person. */
  page(signal?: AbortSignal) {
    return this.client.get<EmergencyLocationsPage>(BASE, signal);
  }
  /** 201; 422 `invalid_network`, 409 `network_exists`, 404 `office_not_found`. */
  addNetwork(body: NewOfficeNetwork) {
    return this.client.post<OfficeNetwork>(`${BASE}/networks`, body);
  }
  /** 204; 404 `network_not_found`. */
  removeNetwork(id: Uuid) {
    return this.client.del<void>(`${BASE}/networks/${enc(id)}`);
  }
  /** Confirms a proposed network at an office. */
  confirmNetwork(id: Uuid, officeId: Uuid) {
    return this.client.post<OfficeNetwork>(`${BASE}/networks/${enc(id)}/confirm`, {
      office_id: officeId,
    });
  }
  /** Dismisses a proposed network; it is never proposed again. */
  dismissNetwork(id: Uuid) {
    return this.client.post<OfficeNetwork>(`${BASE}/networks/${enc(id)}/dismiss`);
  }
  /** Forgets an office's learned position. 204. */
  resetPosition(officeId: Uuid) {
    return this.client.post<void>(`${BASE}/offices/${enc(officeId)}/reset-position`);
  }

  contacts(signal?: AbortSignal) {
    return this.client.get<EmergencyContacts>("/v1/admin/emergency-contacts", signal);
  }
  /** 422 `not_a_member`, `too_many_emails`, `invalid_email`. */
  setContacts(body: SetEmergencyContacts) {
    return this.client.put<EmergencyContacts>("/v1/admin/emergency-contacts", body);
  }
}
